import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

/**
 * Animated counter that counts up from 0 to `value` when scrolled into view.
 * Supports a `suffix` (e.g. "h", "%", "+") appended after the number.
 */
export default function AnimatedCounter({ value, suffix = "", duration = 1.5 }) {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });
    const [display, setDisplay] = useState("0");

    useEffect(() => {
        if (!isInView) return;

        const numericValue = parseInt(value, 10);
        if (isNaN(numericValue)) {
            // non-numeric values (e.g. "High-signal") just appear
            setDisplay(value);
            return;
        }

        const startTime = performance.now();
        const step = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / (duration * 1000), 1);
            // easeOutExpo for punchy feel
            const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
            const current = Math.round(eased * numericValue);
            setDisplay(String(current));
            if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }, [isInView, value, duration]);

    return (
        <motion.span
            ref={ref}
            initial={{ opacity: 0, y: 8 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
        >
            {display}{suffix}
        </motion.span>
    );
}
